import { useState, useEffect } from 'react'

interface AdminLoginProps {
  onAuthSuccess: () => void
}

export default function AdminLogin({ onAuthSuccess }: AdminLoginProps) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    // Check if already authenticated
    const isAuthenticated = localStorage.getItem('harbor_admin_authed') === 'true'
    const adminPassword = import.meta.env.VITE_HARBOR_ADMIN_PASSWORD

    if (!adminPassword) {
      // No password set - show dev warning and bypass
      console.warn(
        '⚠️  VITE_HARBOR_ADMIN_PASSWORD is not set. ' +
        'Admin page is accessible without authentication (dev mode only).'
      )
      onAuthSuccess()
      return
    }

    if (isAuthenticated) {
      onAuthSuccess()
      return
    }

    setIsChecking(false)
  }, [onAuthSuccess])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const adminPassword = import.meta.env.VITE_HARBOR_ADMIN_PASSWORD

    if (!adminPassword) {
      // Shouldn't happen if password gate is working, but handle gracefully
      onAuthSuccess()
      return
    }

    if (password === adminPassword) {
      localStorage.setItem('harbor_admin_authed', 'true')
      onAuthSuccess()
    } else {
      setError('Incorrect password')
      setPassword('')
    }
  }

  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Checking authentication...</div>
      </div>
    )
  }

  const adminPassword = import.meta.env.VITE_HARBOR_ADMIN_PASSWORD

  // If no password is set, this component shouldn't render (handled in useEffect)
  // But include a fallback UI just in case
  if (!adminPassword) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Harbor Agent Admin
          </h1>
          <p className="text-gray-600">
            Enter password to access pack management
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter admin password"
              autoFocus
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="w-full btn-primary py-3 text-lg"
          >
            Enter
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            This is a lightweight UI gate. For production, consider implementing
            proper authentication.
          </p>
        </div>
      </div>
    </div>
  )
}

